import { useState } from "react";
import { Truck, MessageCircle, CheckCircle2, MapPin, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fmtBRL, buildWhatsAppUrl } from "@/lib/format";
import { useSettings } from "@/context/SettingsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const onlyDigits = (v) => (v || "").replace(/\D/g, "");
const maskCEP = (v) => onlyDigits(v).slice(0, 8).replace(/(\d{5})(\d)/, "$1-$2");

export const ShippingCalculator = () => {
  const settings = useSettings();
  const [cep, setCep] = useState(() => localStorage.getItem("hg_delivery_cep") || "");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const calc = async (e) => {
    e?.preventDefault();
    const digits = onlyDigits(cep);
    if (digits.length !== 8) {
      toast.error("Digite um CEP válido com 8 números.");
      return;
    }
    setLoading(true);
    setResult(null);
    // ViaCEP: identifica cidade e bairro para a verificação de cobertura
    let city = "";
    let neighborhood = "";
    try {
      const res = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
      const data = await res.json();
      if (!data.erro) {
        city = data.localidade || "";
        neighborhood = data.bairro || "";
      }
    } catch {
      /* segue a verificação só com o CEP */
    }
    try {
      const { data } = await api.get("/delivery/check", {
        params: { cep: digits, city, neighborhood },
      });
      setResult({ ...data, city, neighborhood });
      // CEP aproveitado automaticamente no checkout
      localStorage.setItem("hg_delivery_cep", maskCEP(digits));
    } catch {
      toast.error("Não foi possível calcular o frete agora. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const ship = settings.shipping || {};
  const waMsg =
    onlyDigits(cep).length === 8
      ? `Olá! Gostaria de saber se a HG Móveis realiza entregas no meu endereço. Meu CEP é: ${maskCEP(cep)}.`
      : "Olá! Gostaria de saber se a HG Móveis realiza entregas no meu endereço. Vou informar minha cidade e bairro.";

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-4 sm:p-5" data-testid="shipping-calculator">
      <p className="flex items-center gap-2 font-heading font-semibold text-gray-900 text-sm sm:text-base">
        <Truck className="w-4 h-4 text-brand" /> Calcule o frete para sua região
      </p>
      <form onSubmit={calc} className="mt-3 flex gap-2">
        <Input
          inputMode="numeric"
          value={cep}
          onChange={(e) => setCep(maskCEP(e.target.value))}
          placeholder="Digite seu CEP"
          aria-label="Digite seu CEP"
          className="h-11"
          data-testid="shipping-cep-input"
        />
        <Button
          type="submit"
          disabled={loading}
          className="h-11 rounded-full px-4 sm:px-5 font-bold uppercase text-xs sm:text-sm shrink-0 active:scale-95 transition-transform"
          data-testid="shipping-calc-button"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Calcular Frete"}
        </Button>
      </form>

      {result?.covered && (
        <div className="mt-3 bg-green-50 border border-green-200 rounded-xl p-3.5" data-testid="shipping-result-ok">
          <p className="flex items-center gap-2 text-sm font-semibold text-green-800">
            <CheckCircle2 className="w-4 h-4 shrink-0" /> Entregamos na sua região!
          </p>
          {(result.neighborhood || result.city) && (
            <p className="mt-1 flex items-center gap-1.5 text-xs text-green-700">
              <MapPin className="w-3.5 h-3.5 shrink-0" />
              {[result.neighborhood, result.city].filter(Boolean).join(", ")}
            </p>
          )}
          <p className="mt-1.5 text-sm text-green-800" data-testid="shipping-fee-value">
            {ship.promo_active ? "Frete promocional" : "Taxa de entrega"}: <strong>{fmtBRL(result.fee)}</strong>
          </p>
          <p className="text-[11px] text-green-700 mt-0.5">
            Taxa única por pedido, paga somente na entrega.
          </p>
        </div>
      )}

      {result && !result.covered && (
        <div className="mt-3 bg-amber-50 border border-amber-200 rounded-xl p-3.5" data-testid="shipping-result-unavailable">
          <p className="text-sm text-amber-800 font-medium">
            Infelizmente, ainda não realizamos entregas na sua região.
          </p>
        </div>
      )}

      {/* consulta de entrega pelo WhatsApp — sempre disponível */}
      <div className="mt-3 pt-3 border-t border-gray-100">
        <p className="text-xs text-gray-500">
          Ficou com dúvida se entregamos na sua região? Consulte nossa equipe pelo WhatsApp!
        </p>
        <a
          href={buildWhatsAppUrl(waMsg, settings.whatsapp_number)}
          target="_blank"
          rel="noreferrer"
          className="block"
        >
          <Button
            className="mt-2 w-full rounded-full bg-green-600 hover:bg-green-500 text-white font-bold uppercase text-xs sm:text-sm active:scale-95 transition-transform"
            data-testid="shipping-whatsapp-button"
          >
            <MessageCircle className="w-4 h-4" /> Consultar Entrega pelo WhatsApp
          </Button>
        </a>
      </div>
    </div>
  );
};
